import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { apiFetch } from "../../app/apiClient";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { decreaseFullScreenLoading, increaseFullScreenLoading } from "../../appSlice";
import ComponentLoading from "../../components/ComponentLoading";
import { currentToken, userLoggedIn } from "../login/loginSlice";
import { decodeJwt } from "../login/jwt";
import { Account as AccountData, currentAccount, setAccount, updateDisplayName } from "./accountSlice";
import "./Account.scss";

function Account() {
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(userLoggedIn);
    const token = useAppSelector(currentToken);
    const account = useAppSelector(currentAccount);

    // Read-only identity, derived from the JWT the same way Header.tsx does. Used as an
    // immediate fallback until (or if) the getAccount call below succeeds.
    const claims = token ? decodeJwt(token) : null;
    const claimsUsername = claims?.username ?? claims?.user ?? "";
    const claimsDomain = claims?.domain ?? "";
    const claimsEmail = claimsUsername && claimsDomain ? `${claimsUsername}@${claimsDomain}` : (claims?.user ?? "");

    const [loadingEvents, setLoadingEvents] = useState(0);
    const [displayName, setDisplayName] = useState("");
    const [displayNameValidation, setDisplayNameValidation] = useState(<></>);
    const [profileMessage, setProfileMessage] = useState(<></>);

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [passwordValidation, setPasswordValidation] = useState(<></>);
    const [passwordMessage, setPasswordMessage] = useState(<></>);

    useEffect(() => {
        if (!token) {
            return;
        }
        const abortController = new AbortController();
        setLoadingEvents((count) => count + 1);
        // TODO(ONNAECBackend): wire this up to the real getAccount Lambda once it's live.
        // Expected response shape: { email, username, domain, displayName }.
        apiFetch("/api/account", {
            method: "GET",
            token,
            signal: abortController.signal
        }).then((response) => {
            if (!response.ok) throw new Error("LOAD_FAILED");
            return response.json();
        }).then((data: AccountData) => {
            dispatch(setAccount(data));
            setDisplayName(data.displayName ?? "");
        }).catch((error) => {
            if (error.name === "AbortError" || error.name === "SessionExpiredError") return;
            // getAccount isn't live yet (or failed) - fall back to what we can derive from the JWT.
            setDisplayName(claimsUsername);
        }).finally(() => {
            setLoadingEvents((count) => count - 1);
        });
        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (!isLoggedIn) {
        return <Navigate to="/login" />;
    }

    const saveProfile = async () => {
        const trimmedName = displayName.trim();
        if (trimmedName.length < 2) {
            setDisplayNameValidation(<span className={"validation"}>Please enter a display name (at least 2 characters).</span>);
            return;
        }
        setDisplayNameValidation(<></>);
        setProfileMessage(<></>);
        dispatch(increaseFullScreenLoading());
        try {
            // TODO(ONNAECBackend): wire this up to the real updateAccount Lambda once it's live.
            await apiFetch("/api/account", {
                method: "PUT",
                token,
                body: JSON.stringify({ displayName: trimmedName })
            });
            dispatch(updateDisplayName(trimmedName));
            setProfileMessage(<span className={"success"}>Display name updated.</span>);
        } catch (error: any) {
            if (error.name !== "SessionExpiredError") {
                setProfileMessage(<span className={"validation"}>Sorry, we couldn't save your display name. Please try again.</span>);
            }
        } finally {
            dispatch(decreaseFullScreenLoading());
        }
    };

    const changePassword = async () => {
        if (currentPassword.length < 1) {
            setPasswordValidation(<span className={"validation"}>Please enter your current password.</span>);
            return;
        }
        if (newPassword.length < 8) {
            setPasswordValidation(<span className={"validation"}>Please choose a new password of at least 8 characters.</span>);
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setPasswordValidation(<span className={"validation"}>New password and confirmation do not match.</span>);
            return;
        }
        setPasswordValidation(<></>);
        setPasswordMessage(<></>);
        dispatch(increaseFullScreenLoading());
        try {
            // TODO(ONNAECBackend): wire this up to the real updateAccount Lambda once it's live.
            // Coordinate the exact body shape (currentPassword/newPassword) with that task.
            const response = await apiFetch("/api/account", {
                method: "PUT",
                token,
                treatAuthErrorsAsSessionExpiry: false,
                body: JSON.stringify({ currentPassword, newPassword })
            });
            if (response.status === 401) {
                throw new Error("WRONG_PASSWORD");
            }
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
            setPasswordMessage(<span className={"success"}>Password updated.</span>);
        } catch (error: any) {
            if (error.message === "WRONG_PASSWORD") {
                setPasswordValidation(<span className={"validation"}>Current password is incorrect.</span>);
            } else if (error.name !== "SessionExpiredError") {
                setPasswordMessage(<span className={"validation"}>Sorry, we couldn't update your password. Please try again.</span>);
            }
        } finally {
            dispatch(decreaseFullScreenLoading());
        }
    };

    const email = account?.email ?? claimsEmail;
    const username = account?.username ?? claimsUsername;
    const domain = account?.domain ?? claimsDomain;

    return (<div id={"accountContent"} className={"content"}>
        <ComponentLoading isActive={loadingEvents > 0} />
        <h1>Account Settings</h1>

        <section className={"accountSection"}>
            <h2>Your Details</h2>
            <label>
                Email address:
                <input type="text" value={email} readOnly disabled />
            </label>
            <label>
                Username:
                <input type="text" value={username} readOnly disabled />
            </label>
            <label>
                Domain:
                <input type="text" value={domain} readOnly disabled />
            </label>
            <label>
                Display name:
                <input type="text" placeholder={"Display Name"} value={displayName} onChange={(e) => setDisplayName(e.target.value)} onKeyDown={(k) => { if (k.key === "Enter") { saveProfile(); } }} />
                {displayNameValidation}
            </label>
            {profileMessage}
            <button className={"genericBtn"} onClick={saveProfile}>Save Changes</button>
        </section>

        <section className={"accountSection"}>
            <h2>Change Password</h2>
            <label>
                Current password:
                <input type="password" placeholder={"Current Password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} onKeyDown={(k) => { if (k.key === "Enter") { changePassword(); } }} />
            </label>
            <label>
                New password:
                <input type="password" placeholder={"New Password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} onKeyDown={(k) => { if (k.key === "Enter") { changePassword(); } }} />
            </label>
            <label>
                Confirm new password:
                <input type="password" placeholder={"Confirm New Password"} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} onKeyDown={(k) => { if (k.key === "Enter") { changePassword(); } }} />
            </label>
            {passwordValidation}
            {passwordMessage}
            <button className={"genericBtn"} onClick={changePassword}>Change Password</button>
        </section>
    </div>);
}

export default Account;
